import { compact, isEqual } from 'lodash';
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import {
  getNewFileUploadFromUploadResult,
  NormalizedUploadedFileData,
} from '@/modules/documents/documentUploaderUtils';
import {
  MultiDocumentUploaderContext,
  SelectedExistingDocument,
} from '@/modules/documents/MultiDocumentUploader/context/multiDocumentUploader.context';
import { useInferDocumentTypesLazyQuery } from '@/modules/documents/MultiDocumentUploader/graphql/MultiDocumentUploader.generated';
import type { UploadedFileEntityMapping } from '@/modules/documents/MultiDocumentUploader/types';
import { EntityType } from '@/modules/entities/types/EntityType';
import { UploadedFileData } from '@/modules/files/FileUploaderV2/hooks/useUploadFile';
import { UploadFileV2Mutation } from '@/modules/files/graphql/FileUploaderV2.generated';
import { useCreateDocumentsAndRelationships } from '@/modules/files/hooks/useCreateDocumentsAndRelationships';
import { useAICapabilitiesEnabled } from '@/modules/tenant/TenantDetailsContext/hooks/useAICapabilitiesEnabled';
import {
  DocumentType,
  InferDocumentTypeInput,
  InferDocumentTypeOutput,
} from '@/types/schema';

function useContextValue(): MultiDocumentUploaderContext {
  const { reportError } = useReportError();
  const { showFeedback } = useFeedback();
  const trackUserEvent = useTrackUserEvent();
  const aiCapabilitiesEnabled = useAICapabilitiesEnabled();

  const [getInferredTypes] = useInferDocumentTypesLazyQuery();
  const [createDocuments] = useCreateDocumentsAndRelationships();

  const [selectedExistingDocuments, setSelectedExistingDocuments] = useState<
    SelectedExistingDocument[]
  >([]);

  const [uploadedFiles, setUploadedFiles] = useState<
    NormalizedUploadedFileData[]
  >([]);

  const [fileIDTypeMap, setFileIDTypeMap] = useState<
    Record<string, DocumentType | ''>
  >({});

  const [uploadedFileEntityMap, setUploadedFileEntityMap] = useState<
    Record<string, UploadedFileEntityMapping[]>
  >({});

  const [inferringDocumentTypes, setInferringDocumentTypes] = useState(false);

  const [inferredTypes, setInferredTypes] = useState<InferDocumentTypeOutput[]>(
    []
  );

  const [isUploading, setIsUploading] = useState(false);

  // Lets callers await the end of in-flight uploads. We track `isUploading` in a
  // ref (readable synchronously) and hold the resolvers of any pending
  // `whenUploadsSettle()` promises, flushing them the moment uploads settle.
  const isUploadingRef = useRef(false);
  const settleWaitersRef = useRef<Array<() => void>>([]);
  useEffect(() => {
    isUploadingRef.current = isUploading;
    if (!isUploading && settleWaitersRef.current.length > 0) {
      const waiters = settleWaitersRef.current;
      settleWaitersRef.current = [];
      waiters.forEach((resolve) => resolve());
    }
  }, [isUploading]);
  const whenUploadsSettle = useCallback((): Promise<void> => {
    if (!isUploadingRef.current) return Promise.resolve();
    return new Promise<void>((resolve) => {
      settleWaitersRef.current.push(resolve);
    });
  }, []);

  const [uploaderErrorMessage, setUploaderErrorMessage] = useState<
    string | undefined
  >(undefined);

  const uploadedFileNameToIDMap = useMemo(() => {
    return uploadedFiles.reduce<Record<string, string>>((acc, curr) => {
      acc[curr.fileName] = curr.fileId;
      return acc;
    }, {});
  }, [uploadedFiles]);

  useEffect(() => {
    const inferredTypesByFileId = inferredTypes.reduce<
      Record<string, DocumentType>
    >(
      (acc, curr) => {
        const fileID = uploadedFileNameToIDMap[curr.fileName];
        if (!fileID) return acc;
        acc[fileID] = curr.documentType;
        return acc;
      },
      {} as Record<string, DocumentType>
    );

    if (Object.keys(inferredTypesByFileId).length === 0) return;

    setFileIDTypeMap((prev) => {
      // Only set inferred types for files that don't already have a type.
      // This preserves any manual document type selections the user has made.
      const merged = { ...prev };
      for (const [fileId, docType] of Object.entries(inferredTypesByFileId)) {
        if (merged[fileId] === undefined) {
          merged[fileId] = docType;
        }
      }
      return isEqual(merged, prev) ? prev : merged;
    });
  }, [inferredTypes, uploadedFileNameToIDMap]);

  const clearUploaderErrorMessage = useCallback(() => {
    setUploaderErrorMessage(undefined);
  }, []);

  const handleUploadFilesStarted = useCallback(
    (files: File[]) => {
      setIsUploading(true);
      clearUploaderErrorMessage();

      if (!aiCapabilitiesEnabled) {
        // If tenant-level AI capabilities are not enabled, don't try to infer document types.
        return;
      }

      const input = files.map<InferDocumentTypeInput>((file) => ({
        // Since we're doing this pre-upload, we don't have a file ID yet, so
        // we'll just generate a simple unique-enough ID.
        // We can't use the file name as the ID because it might contain special
        // characters that the LLM escapes non-deterministically.
        id: Math.random().toString(36).slice(2, 11), // 9 characters of alphanumeric
        fileName: file.name,
      }));

      setInferringDocumentTypes(true);

      void getInferredTypes({
        variables: { input },
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
          // Map the inferred types back to the original file names, using the IDs we generated.
          const inferredTypes = data.inferDocumentTypes.map((d) => ({
            ...d,
            fileName: input.find((i) => i.id === d.id)?.fileName || '',
          }));
          setInferredTypes(inferredTypes);
          setInferringDocumentTypes(false);
        },
        onError: (error) => {
          setInferringDocumentTypes(false);
          reportError(
            'MultiDocumentUploader: Could not infer document types',
            error
          );
          showFeedback(
            'Could not infer document types for uploaded files. Please manually set the document types.',
            { autoHideDuration: 1000 }
          );
        },
      });
    },
    [
      aiCapabilitiesEnabled,
      clearUploaderErrorMessage,
      getInferredTypes,
      reportError,
      showFeedback,
    ]
  );

  const handleUploadFilesComplete = useCallback(
    (uploaded: UploadedFileData<UploadFileV2Mutation>[]) => {
      const results = compact(
        uploaded.map(({ file, result, householdId }) =>
          getNewFileUploadFromUploadResult(file, result?.data, householdId)
        )
      );
      setIsUploading(false);
      setUploadedFiles((prev) => [...prev, ...results]);

      if (results.length > 0) {
        trackUserEvent('document files_uploaded', {
          fileCount: results.length,
        });
      }
    },
    [trackUserEvent]
  );

  const handleDeleteFile = useCallback((fileId: string | undefined) => {
    setUploadedFiles((prev) => prev.filter((f) => f.fileId !== fileId));
    // Also clean up any entity mapping and document type for this file
    if (fileId) {
      setUploadedFileEntityMap((prev) => {
        const next = { ...prev };
        delete next[fileId];
        return next;
      });
      setFileIDTypeMap((prev) => {
        const next = { ...prev };
        delete next[fileId];
        return next;
      });
    }
  }, []);

  const setDocumentTypeOfUploadedFile = useCallback(
    (fileId: string, documentType: DocumentType) => {
      setFileIDTypeMap((prev) => ({
        ...prev,
        [fileId]: documentType,
      }));
    },
    []
  );

  const addEntityForUploadedFile = useCallback(
    (fileId: string, mapping: UploadedFileEntityMapping) => {
      setUploadedFileEntityMap((prev) => {
        const existing = prev[fileId] ?? [];
        // Avoid duplicate entries for the same entity
        if (existing.some((m) => m.entityId === mapping.entityId)) {
          return prev;
        }

        return {
          ...prev,
          [fileId]: [...existing, mapping],
        };
      });
    },
    []
  );

  const removeEntityForUploadedFile = useCallback(
    (fileId: string, entityId: string) => {
      setUploadedFileEntityMap((prev) => {
        const existing = prev[fileId] ?? [];
        return {
          ...prev,
          [fileId]: existing.filter((m) => m.entityId !== entityId),
        };
      });
    },
    []
  );

  const setEntitiesForUploadedFile = useCallback(
    (fileId: string, entities: UploadedFileEntityMapping[]) => {
      setUploadedFileEntityMap((prev) => ({ ...prev, [fileId]: entities }));
    },
    []
  );

  const clearEntitiesForUploadedFile = useCallback((fileId: string) => {
    setUploadedFileEntityMap((prev) => {
      const next = { ...prev };
      delete next[fileId];
      return next;
    });
  }, []);

  const uploadDocuments = useCallback(
    async ({
      householdId,
      defaultDocumentType,
      entityId,
      entityType,
      fileIds,
    }: {
      householdId: string;
      defaultDocumentType: DocumentType;
      entityId?: string;
      entityType?: EntityType | undefined | null;
      fileIds?: string[];
    }): Promise<string[]> => {
      // Restrict to the requested files when a subset is given (defaults to all).
      const filesToCreate = fileIds
        ? uploadedFiles.filter((file) => fileIds.includes(file.fileId))
        : uploadedFiles;

      // Convert uploaded files to document creation options
      const documentOptions = filesToCreate.map((file) => {
        const documentType = fileIDTypeMap[file.fileId] || defaultDocumentType;
        const entityMappings = uploadedFileEntityMap[file.fileId] || [];

        const entities = entityMappings.map((m) => ({
          entityId: m.entityId,
          entityType: m.entityType,
        }));

        // If we have a fallback entityId and entityType, and it's not already in the mappings, add it
        if (
          entityId &&
          entityType &&
          !entities.some((e) => e.entityId === entityId)
        ) {
          entities.push({
            entityId,
            entityType,
          });
        }

        return {
          householdId,
          fileId: file.fileId,
          fileName: file.fileName,
          documentType,
          entities: entities,
        };
      });

      // Create new documents with relationships in a single atomic operation
      const { documentIds: createdDocumentIds } =
        await createDocuments(documentOptions);

      return createdDocumentIds;
    },
    [uploadedFiles, fileIDTypeMap, uploadedFileEntityMap, createDocuments]
  );

  const clearUploadedFiles = useCallback(() => {
    setUploadedFiles([]);
    setFileIDTypeMap({});
    setUploadedFileEntityMap({});
    setIsUploading(false);
  }, []);

  return {
    uploadedFiles,
    handleUploadFilesStarted,
    handleUploadFilesComplete,
    handleDeleteFile,
    setDocumentTypeOfUploadedFile,
    uploadDocuments,
    fileIDTypeMap,
    clearUploadedFiles,
    uploaderErrorMessage,
    setUploaderErrorMessage,
    clearUploaderErrorMessage,
    inferringDocumentTypes,
    selectedExistingDocuments,
    setSelectedExistingDocuments,
    uploadedFileEntityMap,
    addEntityForUploadedFile,
    removeEntityForUploadedFile,
    setEntitiesForUploadedFile,
    clearEntitiesForUploadedFile,
    isUploading,
    whenUploadsSettle,
  };
}

export const MultiDocumentUploaderProvider = ({
  children,
}: PropsWithChildren) => {
  const value = useContextValue();

  return (
    <MultiDocumentUploaderContext.Provider value={value}>
      {children}
    </MultiDocumentUploaderContext.Provider>
  );
};
